import { useEffect, useRef, useState } from "react";

import { defaultStampingConfig, type StampingConfig } from "../engine/models";
import { createLocalStore } from "../lib/store/local-store";
import styles from "./App.module.css";
import { getMacAppStoreHref } from "./app-store-url";
import { ConfigRail } from "./components/ConfigRail";
import { DropZone, type LoadedPdf } from "./components/DropZone";
import { MacAppStoreCard } from "./components/MacAppStoreCard";
import { PreviewSurface } from "./components/PreviewSurface";
import { Features } from "./components/Features";
import { PrivacyModal } from "./components/PrivacyModal";
import { SampleStrip } from "./components/SampleStrip";
import { useStampedPreview } from "./hooks/useStampedPreview";

const SUPPORT_EMAIL = "imprint-support@starkly.cc";
const HOME_URL = "https://imprint.starkly.cc/";
const PRIVACY_URL = "https://starkly.cc/apps/imprint/privacy-policy/";

function isSupportPath(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.pathname.replace(/\/+$/, "") === "/support";
}

function SupportPage() {
  const appStoreHref = getMacAppStoreHref();
  const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=Imprint%20support`;

  return (
    <div className={styles.supportPage}>
      <header className={styles.chrome}>
        <span className={styles.wordmark}>Imprint</span>
      </header>

      <main className={styles.supportMain}>
        <div className={styles.supportInner}>
          <p className={styles.supportEyebrow}>Support</p>
          <h1 id="support-title" className={styles.supportTitle}>
            How can we <span className={styles.supportTitleEmphasis}>help?</span>
          </h1>
          <p className={styles.supportLead}>
            Questions, bug reports, and feature requests go straight to a real person. Imprint
            runs entirely on your Mac, so we never see your documents.
          </p>

          <section className={styles.supportCard} aria-label="Contact support">
            <p className={styles.supportCardLabel}>Email support</p>
            <a className={styles.supportEmail} href={mailtoHref}>
              {SUPPORT_EMAIL}
            </a>
            <p className={styles.supportCardHint}>
              Tell us your macOS version and what you were doing, and we can help faster.
            </p>
            <a className={styles.supportCalloutButton} href={mailtoHref}>
              Contact support
            </a>
          </section>

          <nav className={styles.supportLinks} aria-label="More links">
            <a className={styles.supportLink} href={appStoreHref}>
              Get Imprint for Mac
            </a>
            <span className={styles.supportLinkDivider} aria-hidden="true">·</span>
            <a className={styles.supportLink} href={HOME_URL}>
              Home
            </a>
            <span className={styles.supportLinkDivider} aria-hidden="true">·</span>
            <a className={styles.supportLink} href={PRIVACY_URL} target="_blank" rel="noreferrer">
              Privacy policy
            </a>
          </nav>
        </div>
      </main>
    </div>
  );
}

export function App() {
  if (isSupportPath()) return <SupportPage />;

  const [loaded, setLoaded] = useState<LoadedPdf | null>(null);

  const storeRef = useRef(createLocalStore());
  const [config, setConfig] = useState<StampingConfig>(() => {
    try {
      return storeRef.current.load();
    } catch {
      return defaultStampingConfig();
    }
  });

  useEffect(() => {
    storeRef.current.save(config);
  }, [config]);

  const { stamped, status } = useStampedPreview(loaded, config);

  const previewRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!loaded) return;
    const el = previewRef.current;
    if (!el || typeof el.scrollIntoView !== "function") return;

    const vh = window.innerHeight || 0;
    const rect = el.getBoundingClientRect();
    const mostlyVisible = rect.top >= 0 && rect.top <= vh * 0.5;
    if (mostlyVisible) return;

    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });

    const settle = styles.justLoaded;
    if (settle) {
      el.classList.remove(settle);
      void el.offsetWidth;
      el.classList.add(settle);
    }
  }, [loaded]);

  const [privacyOpen, setPrivacyOpen] = useState(false);
  const appStoreHref = getMacAppStoreHref();

  return (
    <div className={styles.app}>
      <header className={styles.chrome}>
        <span className={styles.wordmark}>Imprint</span>
      </header>

      <section className={styles.hero} aria-labelledby="hero-title">
        <div className={styles.heroCopy}>
          <h1 id="hero-title" className={styles.heroTitle}>
            Bates stamp your PDFs on <span className={styles.heroEmphasis}>Mac</span>.
          </h1>
          <p className={styles.heroSub}>
            The Mac app numbers every file in one pass. Try one in your browser.
          </p>
          <div className={styles.heroCta}>
            <a className={styles.heroCtaButton} href={appStoreHref}>
              Get Imprint for Mac
            </a>
            <span className={styles.heroCtaNote}>
              On the Mac App Store. One time purchase to unlock all features, no subscription.
            </span>
          </div>
        </div>
        <div className={styles.heroAction}>
          <DropZone
            onFile={setLoaded}
            loadedName={loaded?.name ?? null}
            onClear={() => setLoaded(null)}
          />
          <SampleStrip onPick={setLoaded} hasFile={loaded !== null} />
        </div>
      </section>

      <div className={styles.workspaceFrame}>
        <main className={styles.workspace}>
          <div className={styles.inspector}>
            <ConfigRail config={config} onChange={setConfig} />
          </div>

          <div
            ref={previewRef}
            className={[styles.previewPane, loaded ? null : styles.previewIdle]
              .filter(Boolean)
              .join(" ")}
          >
            <PreviewSurface
              stamped={stamped}
              original={loaded?.bytes ?? null}
              stamping={status === "stamping"}
              errored={status === "error"}
            />
          </div>
        </main>

        <div className={styles.actionBar} aria-label="Workspace status">
          <span className={styles.actionStatus}>
            {status === "error"
              ? "We couldn't stamp this PDF. Try another."
              : status === "stamping"
                ? "Stamping in your browser…"
                : stamped
                  ? "Stamp baked into the page."
                  : "Drop a PDF to see it stamp."}
          </span>
          <span className={styles.actionPrivacy}>Processed in your browser. Never uploaded.</span>
        </div>
      </div>

      <section className={styles.demandBand} aria-label="Get Imprint for Mac">
        <div className={styles.demandWrap}>
          <MacAppStoreCard />
        </div>
      </section>

      <section className={styles.belowFold} aria-label="Why Imprint">
        <Features onOpenPrivacy={() => setPrivacyOpen(true)} />

        <section className={styles.closingCta} aria-label="Get Imprint for Mac">
          <p className={styles.closingCtaText}>
            See it on your own document, then get it on your Mac.
          </p>
          <a className={styles.closingCtaButton} href={appStoreHref}>
            Get Imprint for Mac
          </a>
        </section>

        <footer className={styles.footer}>
          <p className={styles.privacyPromise}>
            Processed entirely in your browser. We never see your file.
          </p>
          <button type="button" className={styles.privacyLink} onClick={() => setPrivacyOpen(true)}>
            Read the privacy report
          </button>
          <p className={styles.watermark}>
            Stamped with <a href="https://imprint.starkly.cc">Imprint</a> at imprint.starkly.cc
          </p>
        </footer>
      </section>

      <PrivacyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </div>
  );
}
